export const MOBILE_WORK_LOCATIONS = [
  { code: 'WFH_APP', label: 'Work From Home (WFH)', description: 'Working from home for this attendance day.' },
  { code: 'CLIENT_OFFICE', label: 'Client Office', description: 'Working from a client office.' },
  { code: 'CLIENT_PROSPECT_VISIT', label: 'Client/Prospect Visit', description: 'Visiting one or more client or prospect locations.' },
] as const;

export type MobileWorkLocationCode = (typeof MOBILE_WORK_LOCATIONS)[number]['code'];
export type AttendanceLocationMode = 'Office' | 'Work From Home (WFH)' | 'Client Office' | 'Client/Prospect Visit' | 'Manual';
export type AttendancePunchLocation = { source?: string | null; locationName?: string | null; timestamp?: string | null; localTimestamp?: string | null };

const OFFICE_SOURCES = new Set(['OFFICE', 'OFFICE_KIOSK', 'FACE_KIOSK', 'KIOSK', 'FACE']);
const LOCATION_LABELS: Record<string, AttendanceLocationMode> = {
  WFH: 'Work From Home (WFH)', WFH_APP: 'Work From Home (WFH)',
  CLIENT_OFFICE: 'Client Office', CLIENT_PROSPECT_VISIT: 'Client/Prospect Visit',
  CLIENT_VISIT: 'Client/Prospect Visit', PROSPECT_VISIT: 'Client/Prospect Visit',
};
const LEGACY_MANUAL_SOURCES = new Set(['MANUAL', 'MOBILE_APP', 'WEB_PORTAL', 'IMPORT', 'SYSTEM']);

function normalizeSource(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toUpperCase().replace(/[\s/-]+/g, '_') : '';
}

function punchHasLocationSignal(punch: AttendancePunchLocation | null | undefined): boolean {
  return Boolean(punch && (punch.source || punch.locationName));
}

/** Kiosk / named office punch. Mobile work-location codes are never office. */
export function isOfficePunch(punch: AttendancePunchLocation | null | undefined): boolean {
  if (!punch) return false;
  const source = normalizeSource(punch.source);
  if (LOCATION_LABELS[source] || LEGACY_MANUAL_SOURCES.has(source)) return false;
  if (OFFICE_SOURCES.has(source)) return true;
  return Boolean(punch.locationName?.trim());
}

export function attendanceLocationModeLabel(mode: string | null | undefined): AttendanceLocationMode | null {
  const source = normalizeSource(mode);
  if (!source) return null;
  if (LOCATION_LABELS[source]) return LOCATION_LABELS[source];
  if (OFFICE_SOURCES.has(source)) return 'Office';
  return 'Manual';
}

/** Converts a stored daily mode back to the source expected by punch endpoints. */
export function attendanceModeToPunchSource(mode: string | null | undefined): MobileWorkLocationCode | null {
  const source = normalizeSource(mode);
  if (source === 'WFH' || source === 'WFH_APP') return 'WFH_APP';
  if (source === 'CLIENT_OFFICE') return 'CLIENT_OFFICE';
  if (source === 'CLIENT_PROSPECT_VISIT' || source === 'CLIENT_VISIT' || source === 'PROSPECT_VISIT') return 'CLIENT_PROSPECT_VISIT';
  return null;
}

/**
 * Work-location codes stay as selected at check-in.
 * Otherwise the platform default (MOBILE_APP / WEB_PORTAL) is used — never invent kiosk.
 */
export function resolveSelfServicePunchSource(
  mode: string | null | undefined,
  platformDefault: string,
): string {
  return attendanceModeToPunchSource(mode) ?? platformDefault;
}

/** Prefer the explicit work-location code selected at check-in. */
export function resolveAttendanceLocationMode(input: {
  attendanceMode?: string | null;
  checkIn?: AttendancePunchLocation | null;
  checkOut?: AttendancePunchLocation | null;
}): AttendanceLocationMode | null {
  const checkInHasSignal = punchHasLocationSignal(input.checkIn);
  const checkOutHasSignal = punchHasLocationSignal(input.checkOut);
  if (checkInHasSignal) {
    const inLabel = attendanceLocationModeLabel(input.checkIn?.source);
    const outLabel = attendanceLocationModeLabel(input.checkOut?.source);
    if (inLabel && inLabel !== 'Manual' && inLabel !== 'Office' && (!checkOutHasSignal || outLabel === inLabel)) return inLabel;
    if (isOfficePunch(input.checkIn) && (!checkOutHasSignal || isOfficePunch(input.checkOut))) return 'Office';
    return 'Manual';
  }
  return attendanceLocationModeLabel(input.attendanceMode);
}
